/**
 * Slot Injection Processor
 * Processes collection sections and injects slot objects based on row count calculations
 */
import type { Slot } from '~/types/collection';

/**
 * Returns true when a placeholder should NOT have slots injected inside it,
 * receiving a single slot after its set instead (coarse placement). This is the
 * case when the placeholder has no rowlayout, or a non-empty backgroundColor.
 */
function placeholderSkipsInnerSlots (placeholder: any): boolean {
  return !placeholder.fields?.rowlayout || !!placeholder.fields?.backgroundColor;
}

/**
 * Calculates total row count for a cpp-placeholder.
 * Row count is independent of the simple/multi-row distinction — it is always
 * derived from the layout:
 * - No rowlayout → 1 row
 * - Otherwise → expand fields.rowlayout by fields.repeat (defaults to 1) and
 *   sum the row count of each expanded layout entry
 */
function calculatePlaceholderRows (placeholder: any): number {
  const rowlayout = placeholder.fields?.rowlayout;

  if (!rowlayout) {
    return 1;
  }

  const repeat = placeholder.fields?.repeat || 1;
  const { activeLayouts } = getRowLayout(rowlayout, repeat);

  return activeLayouts.reduce((sum: number, layout: string) => sum + calculateSetRows({ layout, items: [] }), 0);
}

/**
 * Builds the single slot object injected AFTER a simple placeholder's set
 * (no slots are placed inside the placeholder). Advances the global slotIndex
 * by the placeholder's full row count and tracks the last slot name (old logic).
 */
function buildSimplePlaceholderSlot (
  placeholder: any,
  slotPrefix: string,
  slotIndex: { value: number },
  availableSlots: string[]
): Slot {
  const rowCount = calculatePlaceholderRows(placeholder);
  const lastSlotName = `${slotPrefix}_${slotIndex.value + rowCount - 1}`;

  slotIndex.value += rowCount;
  availableSlots.push(lastSlotName);

  return {
    type: 'slot',
    slotName: lastSlotName
  };
}

/**
 * Builds the slot objects injected inside a multi-row cpp-placeholder.
 * Expands fields.rowlayout by fields.repeat (defaults to 1), counts rows per
 * layout entry, and creates one slot per row. relativePosition resets to 1 at
 * the start of each layout entry (each entry behaves like its own set).
 * Advances the global slotIndex and tracks every slot name in availableSlots.
 */
function buildPlaceholderSlots (
  placeholder: any,
  slotPrefix: string,
  slotIndex: { value: number },
  availableSlots: string[]
): Slot[] {
  const repeat = placeholder.fields?.repeat || 1;
  const { activeLayouts } = getRowLayout(placeholder.fields?.rowlayout || '', repeat);
  const slots: Slot[] = [];

  for (const layout of activeLayouts) {
    const rowCount = calculateSetRows({ layout, items: [] });
    for (let i = 0; i < rowCount; i++) {
      const slotName = `${slotPrefix}_${slotIndex.value}`;
      slots.push({
        type: 'slot',
        relativePosition: i + 1,
        slotName
      });
      availableSlots.push(slotName);
      slotIndex.value++;
    }
  }

  return slots;
}

/**
 * Injects a slots array inside a multi-row placeholder's cpp-placeholder object.
 * The wrapping set keeps its shape; only the placeholder item gains a `slots` array.
 */
function injectSlotsIntoPlaceholderSet (
  set: any,
  slotPrefix: string,
  slotIndex: { value: number },
  availableSlots: string[]
): any {
  const placeholder = set.items[0];
  const slots = buildPlaceholderSlots(placeholder, slotPrefix, slotIndex, availableSlots);

  return {
    ...set,
    items: [
      {
        ...placeholder,
        slots
      }
    ]
  };
}

/**
 * Calculates total row count for all sets in a group
 */
function calculateGroupRows (group: any): number {
  if (!group.items || !Array.isArray(group.items)) {
    return 0;
  }

  let totalRows = 0;

  for (const item of group.items) {
    if (item.type === 'set') {
      // Check if it's a cpp-placeholder
      if (item.items?.length === 1 && item.items[0].type === 'cpp-placeholder') {
        totalRows += calculatePlaceholderRows(item.items[0]);
      } else {
        totalRows += calculateSetRows(item);
      }
    } else if (item.type === 'group') {
      // Recursively calculate for nested groups
      totalRows += calculateGroupRows(item);
    }
  }

  return totalRows;
}

/**
 * Injects slots into a set's items array
 */
function injectSlotsIntoSet (
  set: any,
  slotPrefix: string,
  slotIndex: { value: number },
  availableSlots: string[]
) {
  const rowCount = calculateSetRows(set);
  const newItems = [...set.items];

  // Inject N slots for N rows
  for (let i = 0; i < rowCount; i++) {
    const slotName = `${slotPrefix}_${slotIndex.value}`;
    const slotObject = {
      type: 'slot',
      relativePosition: i + 1,
      slotName
    };
    availableSlots.push(slotName);
    slotIndex.value++;
    newItems.push(slotObject);
  }

  return {
    ...set,
    items: newItems
  };
}

/**
 * Processes a group based on allowAds flag
 * If allowAds: true, injects slots inside each set within the group
 * If allowAds: false/undefined, returns group as-is (slots injected after group)
 */
function processGroupItems (
  group: any,
  slotPrefix: string,
  slotIndex: { value: number },
  availableSlots: string[]
): any {
  const allowAds = group.data?.allowAds === true;

  if (!allowAds) {
    /*
     * If allowAds is not true, return group as-is
     * Slots will be injected after the group at parent level
     */
    return group;
  }

  // If allowAds is true, process each set inside the group to inject slots
  const processedItems: any[] = [];

  for (const item of group.items) {
    if (item.type === 'set') {
      // Check if it's a cpp-placeholder
      if (item.items?.length === 1 && item.items[0].type === 'cpp-placeholder') {
        /*
         * For cpp-placeholder inside allowAds group, just increment the slot counter
         * Do NOT inject any slot after the placeholder
         */
        const placeholder = item.items[0];

        if (placeholderSkipsInnerSlots(placeholder)) {
          /*
           * Simple placeholder - no slots inside; advance by full row count and
           * inject ONE slot after the set within the group using the last slot name
           */
          processedItems.push(item);
          processedItems.push(buildSimplePlaceholderSlot(placeholder, slotPrefix, slotIndex, availableSlots));
        } else {
          // Multi-row placeholder - inject slots array inside the placeholder
          processedItems.push(injectSlotsIntoPlaceholderSet(item, slotPrefix, slotIndex, availableSlots));
        }
      } else {
        // Regular set inside allowAds group - inject slots inside the set
        processedItems.push(injectSlotsIntoSet(item, slotPrefix, slotIndex, availableSlots));
      }
    } else if (item.type === 'group') {
      // Recursively process nested groups
      processedItems.push(processGroupItems(item, slotPrefix, slotIndex, availableSlots));
    } else {
      processedItems.push(item);
    }
  }

  return {
    ...group,
    items: processedItems
  };
}

/**
 * Main processor function that injects slots into a collection section
 * @param section - The collection array (sectionOne or sectionTwo)
 * @param sectionType - 'top' or 'bottom' to determine slot prefix
 * @returns Object with modified section and available slots list
 */
export function injectSlots (
  section: any[],
  sectionType: string
): { modifiedSection: any[]; availableSlots: string[] } {
  if (!section || section.length === 0) {
    return { modifiedSection: section, availableSlots: [] };
  }

  const slotPrefix = sectionType === 'top' ? 'top_slot' : 'bottom_slot';
  const slotIndex = { value: 1 }; // Start from 1
  const availableSlots: string[] = [];
  const result = [];

  for (const item of section) {
    if (item.type === 'group') {
      const group = item;
      const allowAds = group.data?.allowAds === true;
      const disallowAdsBelow = group.data?.disallowAdsBelow === true;

      if (allowAds) {
        // Rule 1a: Group with allowAds: true - inject slots inside each set
        const processedGroup = processGroupItems(group, slotPrefix, slotIndex, availableSlots);
        result.push(processedGroup);
        // NO slot injected after the group
      } else {
        // Rule 1b: Group without allowAds or allowAds: false - inject ONE slot after
        const totalRows = calculateGroupRows(item);

        if (totalRows > 0) {
          // Use the current value as the LAST slot name
          const lastSlotName = `${slotPrefix}_${slotIndex.value + totalRows - 1}`;

          // Increment slot index by totalRows
          slotIndex.value += totalRows;

          // Keep group as-is (no internal modifications)
          result.push(item);

          // Only add the ONE injected slot to availableSlots if disallowAdsBelow is not true
          if (!disallowAdsBelow) {
            // Only add the ONE injected slot to availableSlots
            availableSlots.push(lastSlotName);
            const slotObject = {
              type: 'slot',
              slotName: lastSlotName
            };
            result.push(slotObject);
          }
        } else {
          // No rows, just push the group as-is
          result.push(item);
        }
      }
    } else if (item.type === 'set') {
      // Check if it's a cpp-placeholder
      if (item.items?.length === 1 && item.items[0].type === 'cpp-placeholder') {
        // Rule 2: Standalone cpp-placeholder
        const placeholder = item.items[0];

        if (placeholderSkipsInnerSlots(placeholder)) {
          /*
           * Simple placeholder - no slots inside; advance by full row count and
           * inject ONE slot after the set using the last slot name (old logic)
           */
          result.push(item);
          result.push(buildSimplePlaceholderSlot(placeholder, slotPrefix, slotIndex, availableSlots));
        } else {
          // Multi-row placeholder - inject slots array inside the placeholder
          result.push(injectSlotsIntoPlaceholderSet(item, slotPrefix, slotIndex, availableSlots));
        }
      } else {
        // Rule 3: Regular standalone set - inject slots inside
        const processedSet = injectSlotsIntoSet(item, slotPrefix, slotIndex, availableSlots);
        result.push(processedSet);
      }
    } else {
      // Unknown type, keep as is
      result.push(item);
    }
  }

  return {
    modifiedSection: result,
    availableSlots
  };
}

/**
 * Processes both sections with slot injection
 * @param sectionOne - First section array
 * @param sectionTwo - Second section array
 * @returns Object with processed sections and combined available slots
 */
export function processSectionsWithSlotInjection (sectionOne: any[], sectionTwo: any[]) {
  const topResult = injectSlots(sectionOne, 'top');
  const bottomResult = injectSlots(sectionTwo, 'bottom');

  return {
    sectionOne: topResult.modifiedSection,
    sectionTwo: bottomResult.modifiedSection,
    topSlots: topResult.availableSlots,
    bottomSlots: bottomResult.availableSlots
  };
}
